/*
  <predictive-search> wraps a search form and shows suggestions under the field
  as the shopper types. Suggestions are rendered by sections/predictive-search.liquid
  and follow the combobox pattern: focus stays in the field while the arrow keys
  move through the options.
*/
(function () {
  if (customElements.get('predictive-search')) return;

  const RESOURCE_QUERY = 'resources[type]=query,product,collection,page,article&resources[limit]=4&resources[limit_scope]=each';

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.status = this.querySelector('[data-predictive-status]');
      const theme = window.Theme;
      if (!this.input || !this.results || !theme || !theme.routes.predictiveSearch) return;

      this.cache = new Map();
      this.controller = null;
      this.options = [];
      this.activeIndex = -1;

      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-haspopup', 'listbox');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-controls', this.results.id);
      this.results.setAttribute('role', 'listbox');
      this.results.setAttribute('aria-label', this.dataset.resultsLabel || '');

      const request = theme.debounce(() => this.request(), 250);
      this.input.addEventListener('input', request);
      this.input.addEventListener('focus', () => {
        if (this.options.length && this.input.value.trim()) this.open();
      });
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.close();
      });
      /* Keeps focus in the field when an option is pressed, so the click lands. */
      this.results.addEventListener('mousedown', (event) => event.preventDefault());
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
    }

    get isOpen() {
      return !this.results.hidden;
    }

    async request() {
      const terms = this.input.value.trim();
      if (this.controller) this.controller.abort();
      this.controller = null;
      /* The aborted request no longer owns the busy state, so clear it here. */
      this.results.removeAttribute('aria-busy');

      if (!terms) {
        this.clear();
        return;
      }

      if (this.cache.has(terms)) {
        this.render(this.cache.get(terms));
        return;
      }

      const controller = new AbortController();
      this.controller = controller;
      this.setStatus(this.dataset.loadingText);
      this.results.setAttribute('aria-busy', 'true');

      try {
        const url = `${window.Theme.routes.predictiveSearch}?q=${encodeURIComponent(terms)}&${RESOURCE_QUERY}`;
        const html = await window.Theme.fetchSection(url, 'predictive-search', { signal: controller.signal });
        if (controller.signal.aborted) return;
        this.cache.set(terms, html);
        this.render(html);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.clear();
      } finally {
        if (this.controller === controller) {
          this.controller = null;
          this.results.removeAttribute('aria-busy');
        }
      }
    }

    render(html) {
      const payload = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-predictive-payload]');
      if (!payload) {
        this.clear();
        return;
      }

      this.results.replaceChildren(...payload.childNodes);
      this.options = Array.from(this.results.querySelectorAll('[role="option"]'));
      this.options.forEach((option, index) => {
        option.id = `${this.results.id}-option-${index}`;
        option.tabIndex = -1;
      });
      this.setActive(-1);
      this.open();
      this.setStatus(payload.dataset.status);
    }

    open() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.setActive(-1);
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
    }

    clear() {
      this.close();
      this.results.replaceChildren();
      this.options = [];
      this.setStatus('');
    }

    setStatus(text) {
      if (this.status) this.status.textContent = text || '';
    }

    setActive(index) {
      this.activeIndex = index;
      this.options.forEach((option, position) => {
        const active = position === index;
        option.setAttribute('aria-selected', String(active));
        option.classList.toggle('is-active', active);
        if (active) option.scrollIntoView({ block: 'nearest' });
      });
      if (index >= 0) this.input.setAttribute('aria-activedescendant', this.options[index].id);
      else this.input.removeAttribute('aria-activedescendant');
    }

    move(step) {
      const count = this.options.length;
      if (!count) return;
      if (!this.isOpen) this.open();
      let next = this.activeIndex + step;
      if (next < 0) next = count - 1;
      if (next >= count) next = 0;
      this.setActive(next);
    }

    onKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          if (!this.options.length) return;
          event.preventDefault();
          this.move(1);
          break;
        case 'ArrowUp':
          if (!this.options.length) return;
          event.preventDefault();
          this.move(-1);
          break;
        case 'Enter':
          if (this.isOpen && this.activeIndex >= 0) {
            event.preventDefault();
            this.options[this.activeIndex].click();
          }
          break;
        case 'Escape':
          if (this.isOpen) {
            event.stopPropagation();
            this.close();
          }
          break;
        default:
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
